import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { createContext, useContext, useRef, useState, useReducer, useEffect, } from 'react';
import useResizeObserver from 'use-resize-observer';
import { useOnOff } from '../hooks/useOnOff';
import { useSplitPaneSize } from './useSplitPaneSize';
const splitPaneContext = createContext(false);
export function useSplitPaneContext() {
    return useContext(splitPaneContext);
}
export function SplitPane(props) {
    const { direction = 'horizontal', controlledSide = 'start', size = '50%', closed = false, onResize, onToggle, children, } = props;
    const minimumSize = typeof closed === 'number' ? closed : null;
    // Whether the pane is explicitly closed. If the value is `false`, the pane
    // may still be currently closed because it is smaller than the minimum size.
    const [isPaneClosed, closePane, openPane] = useOnOff(typeof closed === 'boolean' ? closed : false);
    // Whether the user has already interacted with the pane.
    const [hasTouched, touch] = useReducer(() => true, false);
    const [[splitSize, sizeType], setSize] = useState(() => parseSize(size));
    useEffect(() => {
        setSize(parseSize(size));
    }, [size]);
    useEffect(() => {
        if (typeof closed === 'boolean') {
            if (closed) {
                closePane();
            }
            else {
                openPane();
            }
        }
    }, [closePane, closed, openPane]);
    const splitterRef = useRef(null);
    const { onMouseDown } = useSplitPaneSize({
        controlledSide,
        direction,
        splitterRef,
        sizeType,
        onSizeChange(value) {
            touch();
            setSize(value);
        },
        onResize,
    });
    const rootSize = useResizeObserver();
    let isFinalClosed = isPaneClosed;
    if (!isFinalClosed &&
        minimumSize !== null &&
        !hasTouched &&
        rootSize.width !== undefined &&
        rootSize.height !== undefined) {
        if (direction === 'horizontal') {
            isFinalClosed = rootSize.width < minimumSize;
        }
        else {
            isFinalClosed = rootSize.height < minimumSize;
        }
    }
    function handleToggle() {
        touch();
        if (isFinalClosed) {
            openPane();
            if (isPaneClosed && onToggle) {
                onToggle(false);
            }
        }
        else {
            closePane();
            if (!isPaneClosed && onToggle) {
                onToggle(true);
            }
        }
    }
    function getSplitSideStyle(side) {
        return getItemStyle(isFinalClosed, controlledSide === side, direction, splitSize, sizeType);
    }
    return (_jsxs("div", { ref: rootSize.ref, style: {
            display: 'flex',
            height: '100%',
            width: '100%',
            [direction === 'horizontal' ? 'minWidth' : 'minHeight']: 0,
            flexDirection: direction === 'horizontal' ? 'row' : 'column',
        }, children: [_jsx(SplitSide, { style: getSplitSideStyle('start'), children: children[0] }), _jsx(Splitter, { onDoubleClick: handleToggle, onMouseDown: isFinalClosed ? undefined : onMouseDown, isFinalClosed: isFinalClosed, direction: direction, splitterRef: splitterRef }), _jsx(SplitSide, { style: getSplitSideStyle('end'), children: children[1] })] }));
}
function Splitter(props) {
    const { onDoubleClick, onMouseDown, direction, isFinalClosed, splitterRef } = props;
    return (_jsx("div", { onDoubleClick: onDoubleClick, onMouseDown: onMouseDown, css: getSeparatorStyle(direction, !isFinalClosed), ref: splitterRef, children: _jsx("div", { style: { fontSize: '0.875em' }, children: direction === 'horizontal' ? _jsx("span", { children: "\u22EE" }) : _jsx("span", { children: "\u22EF" }) }) }));
}
function SplitSide(props) {
    const { style, children } = props;
    return _jsx("div", { style: style, children: children });
}
function parseSize(size) {
    const value = Number.parseFloat(size);
    // remove numbers and dots from the string
    const type = size.replaceAll(/[\d .]/g, '');
    return [value, type];
}
const flexBase = 100;
function percentToFlex(percent) {
    percent /= 100;
    return (flexBase - percent * flexBase) / percent;
}
function getItemStyle(isClosed, isControlledSide, direction, size, type) {
    const isHorizontal = direction === 'horizontal';
    if (isClosed) {
        return isControlledSide
            ? { display: 'none' }
            : { flex: '1 1 0%', display: 'flex' };
    }
    else if (type === '%') {
        return isControlledSide
            ? {
                flex: '100 0 0%',
                display: 'flex',
            }
            : {
                flex: `${percentToFlex(size)} 0 0%`,
                display: 'flex',
                [isHorizontal ? 'minWidth' : 'minHeight']: 0,
            };
    }
    else {
        return isControlledSide
            ? {
                [isHorizontal ? 'width' : 'height']: size,
                display: 'flex',
            }
            : {
                flex: '1 1 0%',
                display: 'flex',
                [isHorizontal ? 'minWidth' : 'minHeight']: 0,
            };
    }
}
function getSeparatorStyle(direction, enabled) {
    return css([
        direction === 'horizontal' && {
            cursor: enabled ? 'ew-resize' : 'pointer',
            width: '10px',
        },
        direction === 'vertical' && {
            height: '10px',
            cursor: enabled ? 'ns-resize' : 'pointer',
        },
        {
            backgroundColor: 'rgb(247, 247, 247)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            userSelect: 'none',
            minWidth: 11,
            ':hover': {
                backgroundColor: 'rgb(223, 223, 223)',
            },
        },
    ]);
}
//# sourceMappingURL=SplitPane.js.map